import React from 'react';

import {defaults} from './';
import {multiArg, dedupe, shallowEqual} from 'polyphony/util';
import {useDelay, useSelector} from 'polyphony/hooks';
import {withNoProps} from 'polyphony/hoc';

const App = withNoProps(() => {
    // Nota Bene: The following useSelector calls don't depend on
    //            the API returning results.
    const loading = useSelector(s => s.loading);
    const Loader = useSelector(s => s.loadingIndicator || defaults.LoadingIndicator);
    const ContentWrapper = useSelector(s => s.contentWrapper || React.Fragment);
    const Error = useSelector(s => s.errorPageComponent || defaults.Error);
    const NotFound = useSelector(s => s.notFoundComponent || defaults.NotFound);
    const error = null; // TODO: HTTP errors and React ErrorBoundary
    const posts = useSelector(s => s.posts); // TODO: factor me out

    // Nota Bene: The following useSelector calls depend on the data
    //            returned by the querying endpoint.
    const isSingular = useSelector(s => !s.loading && s.is.singular);
    const isTerms = useSelector(s => !s.loading && s.is.terms);
    const isNotFound = useSelector(s => !s.loading && s.is['404'] && s.total_count === 0 && (!s.posts || s.posts.length === 0));

    const taxonomies = useSelector(s => {
        if (s.loading) return [];
        if (s.plurality && s.plurality.taxonomy) return [s.plurality.taxonomy];
        return dedupe(s.terms.map(x => x.taxonomy), t => t.name);
    }, shallowEqual);

    const postTypes = useSelector(s => {
        if (s.loading) return [];
        if (s.plurality && s.plurality.post_types) {
            return dedupe(Object.values(s.plurality.post_types), pt => pt.name);
        }
        return dedupe(s.posts.map(x => x.post_type), pt => pt.name);
    }, shallowEqual);

    const postTypeKey = multiArg(postTypes.map(x => x.name));
    const taxonomyKey = multiArg(taxonomies.map(x => x.name));
    const {Post, Archive} = useSelector(s => s.loading && s.post_type ? {} : {...defaults, ...s.post_type[postTypeKey]});
    const {TaxonomyArchive} = useSelector(s => s.loading && s.taxonomy ? {} : {...defaults, ...s.taxonomy[taxonomyKey]});

    // Avoids a flash of the default loader before
    // the child theme sets a new one. Also, if we don't show the
    // loader if it's going to be on screen for a fraction of a second,
    // it just feels better in terms of loading UX.
    const showLoader = useDelay();
    if (loading) return showLoader ? <Loader /> : null;

    if (error) return <Error message={error} />;
    if (isNotFound) return <NotFound />;

    return (
        <ContentWrapper>
            <main role="main" key='content' id="polyphony-content" className="content" style={{overflow: 'hidden', width: "100%"}}>
                {isTerms    ? <TaxonomyArchive key='term_archive' taxonomies={taxonomies} />
               : isSingular ? <Post key="single_post" post={posts[0]} />
                            : <Archive key="post_archive" postTypes={postTypes} />}
            </main>
        </ContentWrapper>
    );
});

App.displayName = 'App';

export default App;

